import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Calendar, Clock, MapPin, Mail, Phone, FileText, Trash2, Loader2, Filter, X, Star, DollarSign } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MyQuotesPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    // Wait for auth context to load
    if (authLoading) return;
    
    if (!isAuthenticated || user?.userType !== 'homeowner') {
      navigate('/');
      return;
    }

    fetchQuotes();
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    // Apply filter
    if (statusFilter === 'all') {
      setFilteredQuotes(quotes);
    } else {
      setFilteredQuotes(quotes.filter(q => q.status === statusFilter));
    }
  }, [statusFilter, quotes]);

  const fetchQuotes = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/quotes?email=${user.email}`);
      const sortedQuotes = response.data.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setQuotes(sortedQuotes);
      setFilteredQuotes(sortedQuotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch quote requests.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;
    
    setDeleteLoading(quoteToDelete.id);
    try {
      await axios.delete(`${API_URL}/api/quotes/${quoteToDelete.id}`);
      
      toast({
        title: 'Quote Deleted',
        description: 'Your quote request has been deleted successfully.'
      });
      
      setQuotes(quotes.filter(q => q.id !== quoteToDelete.id));
      setDeleteModalOpen(false);
      setQuoteToDelete(null);
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete quote request.',
        variant: 'destructive'
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select a star rating.',
        variant: 'destructive'
      });
      return;
    }

    if (!reviewComment.trim()) {
      toast({
        title: 'Comment Required',
        description: 'Please write a review comment.',
        variant: 'destructive'
      });
      return;
    }

    if (reviewComment.trim().length < 10) {
      toast({
        title: 'Review Too Short',
        description: 'Please write at least 10 characters.',
        variant: 'destructive'
      });
      return;
    }

    setReviewLoading(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        providerId: selectedQuote.providerId || 'general',
        customerName: user.name,
        rating: reviewRating,
        comment: reviewComment.trim(),
        date: new Date().toISOString()
      });

      toast({
        title: 'Review Submitted!',
        description: 'Thank you for your feedback.',
        duration: 3000
      });

      // Reset modal state
      setReviewModalOpen(false);
      setReviewRating(0);
      setReviewComment('');
      setHoverRating(0);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit review. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 mr-1" />;
      case 'accepted':
        return <span className="mr-1">✓</span>;
      case 'rejected':
        return <X className="h-4 w-4 mr-1" />;
      case 'completed':
        return <span className="mr-1">✓✓</span>;
      default:
        return null;
    }
  };

  const handlePayNow = async (quote) => {
    if (!quote.packageId) return;

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API_URL}/api/payments/package/checkout`, {
        packageId: quote.packageId,
        quoteId: quote.id,
        originUrl: originUrl,
        email: user.email
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to initiate payment',
        variant: 'destructive'
      });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated || user?.userType !== 'homeowner') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Quote Requests</h1>
          <p className="text-gray-600 mt-2">Track and manage your service quote requests</p>
        </div>

        {/* Filter Bar */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter by status:</span>
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'accepted', 'rejected', 'completed'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                    className={statusFilter === status ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {statusFilter === 'all' ? 'No quote requests yet' : `No ${statusFilter} quotes`}
              </h3>
              <p className="text-gray-600 mb-6">
                {statusFilter === 'all' 
                  ? 'Start by requesting quotes from service providers'
                  : `You don't have any ${statusFilter} quote requests`}
              </p>
              {statusFilter === 'all' && (
                <Button
                  onClick={() => navigate('/request-quote')}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Request a Quote
                </Button>
              )}
              {statusFilter !== 'all' && (
                <Button
                  onClick={() => setStatusFilter('all')}
                  variant="outline"
                >
                  Show All Quotes
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{quote.service}</h3>
                        <Badge className={`${getStatusColor(quote.status)} flex items-center`}>
                          {getStatusIcon(quote.status)}
                          {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          Requested: {new Date(quote.createdAt).toLocaleDateString()}
                        </div>
                        {quote.preferredDate && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                            Preferred: {quote.preferredDate}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-gray-700 mt-3 line-clamp-2">{quote.description}</p>
                    </div>
                    
                    <div className="flex flex-col gap-2 md:ml-6">
                      <Button
                        onClick={() => {
                          setSelectedQuote(quote);
                          setDetailsModalOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        View Details
                      </Button>
                      
                      {quote.status === 'accepted' && quote.packageId && quote.paymentStatus === 'unpaid' && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePayNow(quote);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          size="sm"
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          Pay Now
                        </Button>
                      )}
                      
                      {(quote.status === 'accepted' || quote.status === 'completed') && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuote(quote);
                            setReviewModalOpen(true);
                          }}
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                          size="sm"
                        >
                          <Star className="h-4 w-4 mr-1" />
                          Leave a Review
                        </Button>
                      )}
                      
                      {quote.status === 'pending' && (
                        <Button
                          onClick={() => {
                            setQuoteToDelete(quote);
                            setDeleteModalOpen(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {quotes.length > 0 && (
          <Card className="mt-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{quotes.length}</p>
                  <p className="text-sm text-gray-600">Total Requests</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{quotes.filter(q => q.status === 'pending').length}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{quotes.filter(q => q.status === 'accepted').length}</p>
                  <p className="text-sm text-gray-600">Accepted</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{quotes.filter(q => q.status === 'completed').length}</p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quote Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quote Request Details</DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-xl font-bold text-gray-900">{selectedQuote.service}</h3>
                <Badge className={`${getStatusColor(selectedQuote.status)} flex items-center`}>
                  {getStatusIcon(selectedQuote.status)}
                  {selectedQuote.status.charAt(0).toUpperCase() + selectedQuote.status.slice(1)}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Contact Name</Label>
                  <p className="text-gray-900">{selectedQuote.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Email</Label>
                  <p className="text-gray-900">{selectedQuote.email}</p>
                </div>
                <div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Requested Date</Label>
                  <p className="text-gray-900">{new Date(selectedQuote.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                {selectedQuote.preferredDate && (
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-700">Preferred Service Date</Label>
                    <p className="text-gray-900">{selectedQuote.preferredDate}</p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <p className="text-gray-900 mt-1 p-3 bg-gray-50 rounded-md">{selectedQuote.description}</p>
              </div>
              
              {selectedQuote.packageId && selectedQuote.packagePrice && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-orange-800 font-medium">Service Package Selected</p>
                    <Badge className="bg-orange-100 text-orange-800">
                      ${selectedQuote.packagePrice} + 10% fee
                    </Badge>
                  </div>
                  {selectedQuote.paymentStatus === 'unpaid' && selectedQuote.status === 'accepted' && (
                    <Button
                      onClick={() => handlePayNow(selectedQuote)}
                      className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Pay Now (${(selectedQuote.packagePrice * 1.1).toFixed(2)} CAD)
                    </Button>
                  )}
                  {selectedQuote.paymentStatus === 'paid' && (
                    <p className="text-green-700 text-sm mt-2">✓ Payment completed</p>
                  )}
                </div>
              )}
              
              {selectedQuote.status === 'accepted' && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-green-800 font-medium">✓ This quote has been accepted!</p>
                  <p className="text-green-700 text-sm mt-1">The service provider will contact you soon to discuss next steps.</p>
                </div>
              )}
              
              {selectedQuote.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800 font-medium">This quote was not accepted by the provider.</p>
                  <p className="text-red-700 text-sm mt-1">You can request quotes from other providers.</p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => setDetailsModalOpen(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Close
                </Button>
                {selectedQuote.status === 'pending' && (
                  <Button
                    onClick={() => {
                      setDetailsModalOpen(false);
                      setQuoteToDelete(selectedQuote);
                      setDeleteModalOpen(true);
                    }}
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cancel Request
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Quote Request</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to cancel this quote request? This action cannot be undone.
            </p>
            
            {quoteToDelete && (
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-medium text-gray-900">{quoteToDelete.service}</p>
                <p className="text-sm text-gray-600 mt-1">{quoteToDelete.description}</p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setQuoteToDelete(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={deleteLoading}
              >
                Keep Request
              </Button>
              <Button
                onClick={handleDeleteQuote}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  'Yes, Cancel Request'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={(open) => {
        setReviewModalOpen(open);
        if (!open) {
          setReviewRating(0);
          setReviewComment('');
          setHoverRating(0);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Leave a Review</DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-md p-3">
                <p className="font-medium text-gray-900">{selectedQuote.service}</p>
                <p className="text-sm text-gray-600 mt-1">
                  Share your experience with this service
                </p>
              </div>

              {/* Star Rating */}
              <div>
                <Label className="mb-2">Rating *</Label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-8 w-8 ${
                          star <= (hoverRating || reviewRating)
                            ? 'fill-orange-500 text-orange-500'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {reviewRating > 0 ? `${reviewRating} / 5` : 'Select rating'}
                  </span>
                </div>
              </div>

              {/* Comment */}
              <div>
                <Label className="mb-2">Your Review *</Label>
                <Textarea
                  placeholder="Tell us about your experience with this service provider..."
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {reviewComment.length} characters
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setReviewModalOpen(false);
                    setReviewRating(0);
                    setReviewComment('');
                    setHoverRating(0);
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={reviewLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={reviewLoading || reviewRating === 0 || !reviewComment.trim()}
                >
                  {reviewLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Review'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      <Toaster />
    </div>
  );
};

export default MyQuotesPage;